import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@modules/me';
import { AppLayout } from '../modules/layout';
import { SuspenseSpin } from '../shared/styled/spin';

const MeRouter = lazy(() => import('../modules/me/router/page.router'));

// Page Routers
const UsersRouter = lazy(() => import('../modules/users/router/page.router'));
const DeclarationsRouter = lazy(() => import('../modules/declarations/router/page.router'));
const FlightsRouter = lazy(() => import('../modules/flights/router/page.router'));
const SystemSettingsRouter = lazy(() => import('../modules/system-settings/router/page.router'));
const CouponsRouter = lazy(() => import('../modules/coupons/router/page.router'));
const RefundsRouter = lazy(() => import('../modules/refunds/router/page.router'));
const CashbacksRouter = lazy(() => import('../modules/cashbacks/router/page.router'));
const CashbacksModalRouter = lazy(() => import('../modules/cashbacks/router/modal.router'));
const CargoesRouter = lazy(() => import('../modules/cargoes/router/page.router'));
const BoxesRouter = lazy(() => import('../modules/boxes/router/page.router'));
const ShopNamesRouter = lazy(() => import('../modules/shop-names/router/page.router'));
const ReturnTypesRouter = lazy(() => import('../modules/return-types/router/page.router'));
const CountriesRouter = lazy(() => import('../modules/countries/router/page.router'));
const BranchesRouter = lazy(() => import('../modules/branches/router/page.router'));
const BranchPartnersRouter = lazy(() => import('../modules/branch-partners/router/page.router'));
const PlansRouter = lazy(() => import('../modules/plans/router/page.router'));
const ProductTypesRouter = lazy(() => import('../modules/product-types/router/page.router'));
const RegionsRouter = lazy(() => import('../modules/regions/router/page.router'));
const ModelsRouter = lazy(() => import('../modules/models/router/page.router'));
const StatusesRouter = lazy(() => import('../modules/statuses/router/page.router'));
const ArchiveStatusRouter = lazy(() => import('../modules/archive-status/router/page.router'));
const LogsRouter = lazy(() => import('../modules/logs/router/page.router'));
const NewsRouter = lazy(() => import('../modules/news/router/page.router'));
const FaqRouter = lazy(() => import('../modules/faq/router/page.router'));
const ShopsRouter = lazy(() => import('../modules/shops/router/page.router'));
const AboutRouter = lazy(() => import('../modules/about/router/page.router'));
const TransportationConditionsRouter = lazy(() => import('../modules/transportation-conditions/router/page.router'));
const BannersRouter = lazy(() => import('../modules/banners/router/page.router'));
const PopupsRouter = lazy(() => import('../modules/popups/router/page.router'));
const DeliveryProofsRouter = lazy(() => import('../modules/delivery-proofs/router/page.router'));
const CustomsRouter = lazy(() => import('../modules/customs/router/page.router'));
const UnitedQueuesRouter = lazy(() => import('../modules/united-queues/router/page.router'));
const AzerpostQueuesRouter = lazy(() => import('../modules/azerpost-queues/router/page.router'));
const FinChangesRouter = lazy(() => import('../modules/fin-changes/router/page.router'));
const FailedJobsRouter = lazy(() => import('../modules/failed-jobs/router/page.router'));
const NotifierRouter = lazy(() => import('../modules/notifier/router/page.router'));
const TicketTemplatesRouter = lazy(() => import('../modules/ticket-templates/router/page.router'));
const PartnerBoxesRouter = lazy(() => import('../modules/partner-boxes/router/page.router'));
const PartnerBoxAcceptanceRouter = lazy(() => import('../modules/partner-box-acceptance/router/page.router'));
const PartnerStatisticsRouter = lazy(() => import('../modules/partner-statistics/router/page.router'));
const TelegramBotUsersRouter = lazy(() => import('../modules/telegram-bot-users/router/page.router'));
const TransactionsRouter = lazy(() => import('../modules/transactions/router/page.router'));
const CouriersRouter = lazy(() => import('../modules/couriers/router/page.router'));
const SupportsRouter = lazy(() => import('../modules/supports/router/page.router'));
const UnitedDeclarationsRouter = lazy(() => import('../modules/united-declarations/router/page.router'));
const UnitedReturnsRouter = lazy(() => import('../modules/united-returns/router/page.router'));
const BranchInspectionsRouter = lazy(() => import('../modules/branch-inspections/router/page.router'));
const OrdersRouter = lazy(() => import('../modules/orders/router/page.router'));
const CashFlowRouter = lazy(() => import('../modules/cash-flow/router/page.router'));
const StatisticsRouter = lazy(() => import('../modules/statistics/router/page.router'));
const WarehouseRouter = lazy(() => import('../modules/warehouse/router/page.router'));
const BoxTransfersRouter = lazy(() => import('../modules/box-transfers/router/page.router'));
const AppointmentRouter = lazy(() => import('../modules/appointment/router/page.router'));
const SortingRouter = lazy(() => import('../modules/sorting/router/page.router'));
const SortingModalRouter = lazy(() => import('../modules/sorting/router/modal.router'));

// Modal Routers
const UsersModalRouter = lazy(() => import('../modules/users/router/modal.router'));
const DeclarationsModalRouter = lazy(() => import('../modules/declarations/router/modal.router'));
const FlightsModalRouter = lazy(() => import('../modules/flights/router/modal.router'));
const CargoesModalRouter = lazy(() => import('../modules/cargoes/router/modal.router'));
const BoxesModalRouter = lazy(() => import('../modules/boxes/router/modal.router'));
const ShopNamesModalRouter = lazy(() => import('../modules/shop-names/router/modal.router'));
const ReturnTypesModalRouter = lazy(() => import('../modules/return-types/router/modal.router'));
const PlansModalRouter = lazy(() => import('../modules/plans/router/modal.router'));
const ProductTypesModalRouter = lazy(() => import('../modules/product-types/router/modal.router'));
const RegionsModalRouter = lazy(() => import('../modules/regions/router/modal.router'));
const BranchPartnersModalRouter = lazy(() => import('../modules/branch-partners/router/modal.router'));
const BranchesModalRouter = lazy(() => import('../modules/branches/router/modal.router'));
const CountriesModalRouter = lazy(() => import('../modules/countries/router/modal.router'));
const FaqModalRouter = lazy(() => import('../modules/faq/router/modal.router'));
const NewsModalRouter = lazy(() => import('../modules/news/router/modal.router'));
const ShopsModalRouter = lazy(() => import('../modules/shops/router/modal.router'));
const BannersModalRouter = lazy(() => import('../modules/banners/router/modal.router'));
const PopupsModalRouter = lazy(() => import('../modules/popups/router/modal.router'));
const LogsModalRouter = lazy(() => import('../modules/logs/router/modal.router'));
const CustomsModalRouter = lazy(() => import('../modules/customs/router/modal.router'));
const UnitedQueuesModalRouter = lazy(() => import('../modules/united-queues/router/modal.router'));
const AzerpostQueuesModalRouter = lazy(() => import('../modules/azerpost-queues/router/modal.router'));
const FinChangesModalRouter = lazy(() => import('../modules/fin-changes/router/modal.router'));
const FailedJobsModalRouter = lazy(() => import('../modules/failed-jobs/router/modal.router'));
const PartnerBoxesModalRouter = lazy(() => import('../modules/partner-boxes/router/modal.router'));
const TicketTemplatesModalRouter = lazy(() => import('../modules/ticket-templates/router/modal.router'));
const CouponsModalRouter = lazy(() => import('../modules/coupons/router/modal.router'));
const RefundsModalRouter = lazy(() => import('../modules/refunds/router/modal.router'));
const StatusesModalRouter = lazy(() => import('../modules/statuses/router/modal.router'));
const TransactionsModalRouter = lazy(() => import('../modules/transactions/router/modal.router'));
const CouriersModalRouter = lazy(() => import('../modules/couriers/router/modal.router'));
const CashFlowModalRouter = lazy(() => import('../modules/cash-flow/router/modal.router'));
const UnitedReturnsModalRouter = lazy(() => import('../modules/united-returns/router/modal.router'));
const BranchInspectionsModalRouter = lazy(() => import('../modules/branch-inspections/router/modal.router'));
const SupportsModalRouter = lazy(() => import('../modules/supports/router/modal.router'));
const OrdersModalRouter = lazy(() => import('../modules/orders/router/modal.router'));
const StatisticsModalRouter = lazy(() => import('../modules/statistics/router/modal.router'));
const NotifierModalRouter = lazy(() => import('../modules/notifier/router/modal.router'));

export const MainRouter: FC = () => {
  const location = useLocation();
  const { state } = location;
  const { can, canDisplay, state: userState } = useAuth();

  const canDisplayAll = canDisplay('*');
  const canDisplayPartner = canDisplay('partner');
  const canDisplayAny = canDisplayAll || canDisplayPartner;

  if (userState.user.loading) {
    return <SuspenseSpin />;
  }

  if (!userState.user.data) {
    return (
      <Suspense fallback={<SuspenseSpin />}>
        <Routes>
          <Route path="/login" element={<MeRouter />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AppLayout>
      <Suspense fallback={<SuspenseSpin />}>
        {canDisplayAll && (
          <Routes location={state?.background || location}>
            {can('users') && <Route path="/users/*" element={<UsersRouter />} />}
            <Route path="/declarations/*" element={<DeclarationsRouter />} />
            {can('flights') && <Route path="/flights/*" element={<FlightsRouter />} />}
            {can('system_settings') && <Route path="/settings/*" element={<SystemSettingsRouter />} />}
            {can('coupons') && <Route path="/coupons/*" element={<CouponsRouter />} />}
            {can('returns') && <Route path="/refunds/*" element={<RefundsRouter />} />}
            {can('cashback') && <Route path="/cashback/*" element={<CashbacksRouter />} />}
            {can('foreign_cargoes') && <Route path="/cargoes/*" element={<CargoesRouter />} />}
            {can('containers') && <Route path="/boxes/*" element={<BoxesRouter />} />}
            {can('shop_names') && <Route path="/shop-names/*" element={<ShopNamesRouter />} />}
            {can('return_reasons') && <Route path="/return-types/*" element={<ReturnTypesRouter />} />}
            {can('countries') && <Route path="/countries/*" element={<CountriesRouter />} />}
            {can('branches') && <Route path="/branches/*" element={<BranchesRouter />} />}
            {can('companies') && <Route path="/branch-partners/*" element={<BranchPartnersRouter />} />}
            {can('tarifs') && <Route path="/plans/*" element={<PlansRouter />} />}
            {can('producttypes') && <Route path="/product-types/*" element={<ProductTypesRouter />} />}
            {can('regions') && <Route path="/regions/*" element={<RegionsRouter />} />}
            {can('models') && <Route path="/models/*" element={<ModelsRouter />} />}
            {can('states') && <Route path="/status/*" element={<StatusesRouter />} />}
            {can('state_changes') && <Route path="/archive/state/*" element={<ArchiveStatusRouter />} />}
            {can('my_logs') && <Route path="/logs/*" element={<LogsRouter />} />}
            {can('news') && <Route path="/news/*" element={<NewsRouter />} />}
            {can('faq') && <Route path="/faq/*" element={<FaqRouter />} />}
            {can('shops') && <Route path="/shops/*" element={<ShopsRouter />} />}
            {can('about') && <Route path="/about/*" element={<AboutRouter />} />}
            {can('transportation_conditions') && <Route path="/transportation_conditions/*" element={<TransportationConditionsRouter />} />}
            {can('banners') && <Route path="/banners/*" element={<BannersRouter />} />}
            {can('popups') && <Route path="/popups/*" element={<PopupsRouter />} />}
            {can('delivery_proof') && <Route path="/delivery-proofs/*" element={<DeliveryProofsRouter />} />}
            <Route path="/customs/*" element={<CustomsRouter />} />
            {can('united_queues') && <Route path="/united-queues/*" element={<UnitedQueuesRouter />} />}
            {can('azerpost_queues') && <Route path="/azerpost-queues/*" element={<AzerpostQueuesRouter />} />}
            {can('parcel_pin_change') && <Route path="/fin-changes/*" element={<FinChangesRouter />} />}
            {can('dev') && <Route path="/failed-jobs/*" element={<FailedJobsRouter />} />}
            <Route path="/notifier/*" element={<NotifierRouter />} />
            {can('ticket_templates') && <Route path="/ticket-templates/*" element={<TicketTemplatesRouter />} />}
            <Route path="/statistics/branches-partner/*" element={<PartnerStatisticsRouter />} />
            {can('telegram_grant') && <Route path="/telegram-bot-users/*" element={<TelegramBotUsersRouter />} />}
            <Route path="/transactions/*" element={<TransactionsRouter />} />
            {can('couriers') && <Route path="/couriers/*" element={<CouriersRouter />} />}
            {can('tickets') && <Route path="/supports/*" element={<SupportsRouter />} />}
            {can('trendyol_declarations') && <Route path="/united-declarations/*" element={<UnitedDeclarationsRouter />} />}
            {can('united_returns') && <Route path="/united-returns/*" element={<UnitedReturnsRouter />} />}
            {can('branch_inspections_list') && <Route path="/branch-inspections/*" element={<BranchInspectionsRouter />} />}
            <Route path="/orders/*" element={<OrdersRouter />} />
            {can('cashflow') && <Route path="/cash-flow/*" element={<CashFlowRouter />} />}
            <Route path="/statistics/*" element={<StatisticsRouter />} />
            {can('local_warehouse') && <Route path="/warehouse/*" element={<WarehouseRouter />} />}
            {can('container_transfers_logs') && <Route path="/box-transfers/*" element={<BoxTransfersRouter />} />}
            {can('client_appointment') && <Route path="/appointment/*" element={<AppointmentRouter />} />}
            <Route path="/sorting/*" element={<SortingRouter />} />
            <Route path="/" element={<Navigate to="/statistics" replace />} />
          </Routes>
        )}
        {canDisplayAny && (
          <Routes location={state?.background || location}>
            <Route path="/partner-boxes/*" element={<PartnerBoxesRouter />} />
            <Route path="/partner/acceptance/box/*" element={<PartnerBoxAcceptanceRouter />} />
          </Routes>
        )}
        {canDisplayAll && (
          <Routes>
            {can('users') && <Route path="/users/*" element={<UsersModalRouter />} />}
            <Route path="/declarations/*" element={<DeclarationsModalRouter />} />
            {can('flights') && <Route path="/flights/*" element={<FlightsModalRouter />} />}
            {can('foreign_cargoes') && <Route path="/cargoes/*" element={<CargoesModalRouter />} />}
            {can('containers') && <Route path="/boxes/*" element={<BoxesModalRouter />} />}
            {can('shop_names') && <Route path="/shop-names/*" element={<ShopNamesModalRouter />} />}
            {can('return_reasons') && <Route path="/return-types/*" element={<ReturnTypesModalRouter />} />}
            {can('tarifs') && <Route path="/plans/*" element={<PlansModalRouter />} />}
            {can('producttypes') && <Route path="/product-types/*" element={<ProductTypesModalRouter />} />}
            {can('regions') && <Route path="/regions/*" element={<RegionsModalRouter />} />}
            {can('companies') && <Route path="/branch-partners/*" element={<BranchPartnersModalRouter />} />}
            {can('branches') && <Route path="/branches/*" element={<BranchesModalRouter />} />}
            {can('countries') && <Route path="/countries/*" element={<CountriesModalRouter />} />}
            {can('faq') && <Route path="/faq/*" element={<FaqModalRouter />} />}
            {can('news') && <Route path="/news/*" element={<NewsModalRouter />} />}
            {can('shops') && <Route path="/shops/*" element={<ShopsModalRouter />} />}
            {can('banners') && <Route path="/banners/*" element={<BannersModalRouter />} />}
            {can('popups') && <Route path="/popups/*" element={<PopupsModalRouter />} />}
            {can('my_logs') && <Route path="/logs/*" element={<LogsModalRouter />} />}
            <Route path="/customs/*" element={<CustomsModalRouter />} />
            {can('united_queues') && <Route path="/united-queues/*" element={<UnitedQueuesModalRouter />} />}
            {can('azerpost_queues') && <Route path="/azerpost-queues/*" element={<AzerpostQueuesModalRouter />} />}
            {can('parcel_pin_change') && <Route path="/fin-changes/*" element={<FinChangesModalRouter />} />}
            {can('dev') && <Route path="/failed-jobs/*" element={<FailedJobsModalRouter />} />}
            {can('notification_templates') && <Route path="/notifier/*" element={<NotifierModalRouter />} />}
            {can('ticket_templates') && <Route path="/ticket-templates/*" element={<TicketTemplatesModalRouter />} />}
            {can('coupons') && <Route path="/coupons/*" element={<CouponsModalRouter />} />}
            {can('returns') && <Route path="/refunds/*" element={<RefundsModalRouter />} />}
            {can('states') && <Route path="/status/*" element={<StatusesModalRouter />} />}
            <Route path="/transactions/*" element={<TransactionsModalRouter />} />
            {can('couriers') && <Route path="/couriers/*" element={<CouriersModalRouter />} />}
            {can('cashflow') && <Route path="/cash-flow/*" element={<CashFlowModalRouter />} />}
            <Route path="/united-returns/*" element={<UnitedReturnsModalRouter />} />
            {can('branch_inspections_list') && <Route path="/branch-inspections/*" element={<BranchInspectionsModalRouter />} />}
            {can('tickets') && <Route path="/supports/*" element={<SupportsModalRouter />} />}
            <Route path="/orders/*" element={<OrdersModalRouter />} />
            <Route path="/statistics/*" element={<StatisticsModalRouter />} />
            <Route path="/sorting/*" element={<SortingModalRouter />} />
            {can('cashback') && <Route path="/cashback/*" element={<CashbacksModalRouter />} />}
          </Routes>
        )}
        {canDisplayAny && (
          <Routes>
            <Route path="/partner-boxes/*" element={<PartnerBoxesModalRouter />} />
          </Routes>
        )}
      </Suspense>
    </AppLayout>
  );
};
